import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  hasStoredUserState,
  setUserState,
  autoDetectUserState,
  hasBeenLocationPrompted,
  markLocationPrompted,
  STATE_NAMES,
} from '../../lib/userState';

/**
 * LocationGate — first-visit "Where are you building?" modal (Simonds/Metricon
 * pattern) PLUS the silent geo-detect fallback for deep pages.
 *
 * SEO-SAFE BY DESIGN:
 *  - Client-side only (post-hydration); absent from the crawler prerender.
 *  - Never redirects / rewrites the URL based on geo (no geo-redirect / cloaking).
 *  - Overlay only: page content stays in the DOM behind it.
 *  - Only on neutral/brand URLs - never on deep city SEO pages.
 *
 * ONCE PER BROWSER: marked the instant it opens, so it only shows on the first
 * visit, never on reload.
 *
 * Green glass surface to match the header state switcher chip.
 */

const NEUTRAL_PATHS = new Set([
  '/', '/builders', '/find-a-builder', '/suppliers', '/trades', '/professionals',
  '/how-it-works', '/how-buildpilot-works', '/whybuildpilot', '/why-buildpilot',
  '/about', '/pricing', '/contact', '/search',
]);

// Every Australian state/territory. SA + VIC are where BuildPilot is live;
// the rest set the state (so the site shows the "coming soon + waitlist"
// callout) when picked.
const STATE_ORDER = ['SA', 'VIC', 'NSW', 'QLD', 'WA', 'TAS', 'ACT', 'NT'];
const LIVE_STATES = new Set(['SA', 'VIC']);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LocationGate = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [suggested, setSuggested] = useState(null);

  const isNeutral = NEUTRAL_PATHS.has(pathname);

  useEffect(() => {
    let cancelled = false;
    if (hasStoredUserState() || hasBeenLocationPrompted()) return;

    if (!isNeutral) {
      autoDetectUserState();
      return;
    }

    (async () => {
      let code = null;
      try {
        const res = await fetch(`${BACKEND}/api/geo/state`, { credentials: 'omit' });
        if (res.ok) {
          const data = await res.json();
          code = (data && data.state ? String(data.state) : '').toUpperCase() || null;
        }
      } catch { /* network/ad-block - open with nothing preselected */ }
      if (cancelled) return;
      if (hasStoredUserState() || hasBeenLocationPrompted()) return;
      setSuggested(code);
      setOpen(true);
      markLocationPrompted();
    })();

    return () => { cancelled = true; };
  }, [pathname, isNeutral]);

  const choose = useCallback((code) => { setUserState(code, 'manual'); setOpen(false); }, []);
  const dismiss = useCallback(() => setOpen(false), []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-gate-title"
      data-testid="location-gate-modal"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-200"
        onClick={dismiss}
        aria-hidden="true"
      />
      {/* Green glass panel */}
      <div
        className="relative w-full sm:max-w-[460px] rounded-t-3xl sm:rounded-[22px] p-6 sm:p-7 text-white overflow-hidden animate-in fade-in slide-in-from-bottom-4 sm:slide-in-from-bottom-1 duration-250 border border-white/25 shadow-[0_24px_70px_-18px_rgba(0,80,40,0.6)]"
        style={{
          background: 'linear-gradient(160deg, rgba(0,175,85,0.82) 0%, rgba(0,140,68,0.82) 100%)',
          backdropFilter: 'blur(22px) saturate(140%)',
          WebkitBackdropFilter: 'blur(22px) saturate(140%)',
        }}
      >
        {/* subtle top sheen for the glass */}
        <div className="absolute inset-x-0 top-0 h-px bg-white/40 pointer-events-none" aria-hidden="true" />
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          data-testid="location-gate-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/75 hover:text-white hover:bg-white/15 transition-colors"
        >
          <X className="w-[18px] h-[18px]" />
        </button>

        <h2 id="location-gate-title" className="text-[21px] sm:text-[22px] font-bold text-white tracking-[-0.01em] leading-tight text-center px-6">
          Where are you building?
        </h2>
        <p className="text-[14px] text-white/80 mt-1.5 mb-6 leading-[1.5] text-center max-w-[340px] mx-auto">
          Pick your state and we'll tailor builders, designs and costs to you.
        </p>

        <div className="flex flex-wrap justify-center gap-2.5" data-testid="location-gate-states">
          {STATE_ORDER.map((code) => {
            const isSuggested = suggested === code;
            const isLive = LIVE_STATES.has(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => choose(code)}
                data-testid={`location-gate-${code.toLowerCase()}`}
                className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[13.5px] font-semibold border transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                  isSuggested
                    ? 'bg-white text-[#0b3d1e] border-white shadow-sm'
                    : 'bg-white/12 text-white border-white/30 hover:bg-white/22 hover:border-white/60'
                }`}
              >
                {isLive && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${isSuggested ? 'bg-[#00a550]' : 'bg-[#8affc1]'}`}
                    aria-hidden="true"
                  />
                )}
                {STATE_NAMES[code]}
              </button>
            );
          })}
        </div>

        <p className="text-[12px] text-white/65 mt-6 leading-[1.5] flex items-center justify-center gap-1.5 text-center">
          <span className="w-1.5 h-1.5 rounded-full bg-[#8affc1] inline-block flex-shrink-0" aria-hidden="true" />
          Live now in SA &amp; VIC &middot; other states join the waitlist.
        </p>
      </div>
    </div>
  );
};

export default LocationGate;
